import React from 'react';
import { copyFor } from 'config';
import { getSolarProjectName } from 'utils/format';
import { getSeason } from 'domain/for-warbler';
import { Text } from '@perch-labs/shrike';
import { Container } from 'styles';
import { isBefore, formatDate } from '@perch-labs/warbler';
import {
  CheckmarkIcon,
  ContentContainer,
  Donut,
  StyledText,
  IconContainer,
  VerticalBar,
  Wrapper,
} from './enrollment-timeline.style';

const getCopy = copyFor('solar.enrollmentTimeline');
const getSponsoredSolarCopy = copyFor(
  'solar.enrollmentTimeline.sponsoredSolar'
);

export enum EnrollmentStatus {
  Pending = 'pending',
  Waitlist = 'waitlist',
}
interface Props {
  stateAbbreviation?: string;
  stateName?: string;
  status: EnrollmentStatus.Pending | EnrollmentStatus.Waitlist;
  project?: string;
  projectStartDate?: string;
  backgroundColor?: string;
  sponsoredSolar?: boolean;
}

export const EnrollmentTimeline = ({
  project,
  projectStartDate,
  status,
  stateAbbreviation,
  backgroundColor = 'secondary',
  sponsoredSolar = false,
  stateName,
}: Props) => {
  const pending = status === EnrollmentStatus.Pending;
  const waitlist = status === EnrollmentStatus.Waitlist;
  const projectIsLive =
    projectStartDate && isBefore(projectStartDate, new Date());
  const waitlistTitleKey:
    | 'waitlist.waitlistActiveLiveTitle'
    | 'waitlist.waitlistActiveTitle'
    | 'waitlist.pendingActiveTitle'
    | 'waitlist.pendingActiveLiveTitle' =
    projectIsLive || (status === 'waitlist' && projectStartDate === undefined)
      ? `waitlist.${status}ActiveLiveTitle`
      : `waitlist.${status}ActiveTitle`;
  const waitlistSubTitleKey = projectIsLive
    ? 'waitlist.subTitleLive'
    : 'waitlist.subTitle';

  return (
    <Wrapper backgroundColor={backgroundColor}>
      <Container flex>
        <IconContainer>
          {pending ? <CheckmarkIcon /> : <Donut />}
          <VerticalBar />
        </IconContainer>
        {!sponsoredSolar && (
          <ContentContainer>
            <StyledText active={pending}>{getCopy('pending.title')}</StyledText>
            {pending && (
              <Text textStyle="paragraph400" margin={{ top: '8px' }}>
                {getCopy('pending.subTitle')}
              </Text>
            )}
          </ContentContainer>
        )}
        {sponsoredSolar && (
          <ContentContainer>
            <StyledText active={pending}>
              {getSponsoredSolarCopy('available.title')}
            </StyledText>
            {pending && (
              <Text textStyle="paragraph400" margin={{ top: '8px' }}>
                {getSponsoredSolarCopy('available.subTitle')}
              </Text>
            )}
          </ContentContainer>
        )}
      </Container>
      <Container flex>
        <IconContainer>
          {waitlist ? <CheckmarkIcon margin="8px 0" /> : <Donut />}
          <VerticalBar />
        </IconContainer>
        {!sponsoredSolar && (
          <ContentContainer>
            <StyledText active={waitlist}>
              {getCopy(waitlistTitleKey, {
                project: project && getSolarProjectName(project),
                season: getSeason(projectStartDate || ''),
                year: formatDate(projectStartDate || '', { format: 'yyyy' }),
              })}
            </StyledText>
            {waitlist && (
              <Text textStyle="paragraph400" margin={{ top: '8px' }}>
                {getCopy(waitlistSubTitleKey)}
              </Text>
            )}
          </ContentContainer>
        )}
        {sponsoredSolar && (
          <ContentContainer>
            <StyledText active={waitlist}>
              {getSponsoredSolarCopy('waitlist.title')}
            </StyledText>
          </ContentContainer>
        )}
      </Container>
      <Container flex>
        <IconContainer>
          <Donut />
        </IconContainer>
        <ContentContainer>
          <StyledText>
            {!sponsoredSolar
              ? getCopy('enrolled.title', { stateAbbreviation })
              : getSponsoredSolarCopy('enrolled.title', { stateName })}
          </StyledText>
        </ContentContainer>
      </Container>
    </Wrapper>
  );
};
